import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/api';
import { FaArrowRight, FaImage } from 'react-icons/fa';
import './AdminServicesSectionEdit.css';

const DEFAULT_FORM = {
  section_label: 'الخدمات والأسعار',
  section_heading: 'استثمارك في مستقبلك الأكاديمي',
};

const AdminServicesSectionEdit = () => {
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [imageUrls, setImageUrls] = useState({
    collage_main_image_url: '',
    collage_secondary_image_url: '',
  });

  const [files, setFiles] = useState({
    main_image: null,
    secondary_image: null,
  });

  const toAbsoluteImageUrl = (maybeUrl) => {
    if (!maybeUrl) return '';
    const u = String(maybeUrl).trim();
    if (!u) return '';
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    return `${API_BASE}${u}`;
  };

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/admin/services-section`, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const s = res.data?.servicesSection;
        if (!s) return;
        setForm({
          section_label: s.section_label ?? DEFAULT_FORM.section_label,
          section_heading: s.section_heading ?? DEFAULT_FORM.section_heading,
        });
        setImageUrls({
          collage_main_image_url: s.collage_main_image_url ?? '',
          collage_secondary_image_url: s.collage_secondary_image_url ?? '',
        });
      })
      .catch((err) => {
        console.error('Failed to load services section:', err);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFile = (field, file) => {
    setFiles((prev) => ({ ...prev, [field]: file }));
  };

  const Preview = ({ url, label }) => {
    if (!url) return <div className="admin-preview-empty">{label}</div>;
    return (
      <div className="admin-preview">
        <img src={toAbsoluteImageUrl(url)} alt={label} />
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('section_label', form.section_label);
      fd.append('section_heading', form.section_heading);

      if (files.main_image) fd.append('main_image', files.main_image);
      if (files.secondary_image)
        fd.append('secondary_image', files.secondary_image);

      await axios.put(`${API_BASE}/api/admin/services-section`, fd, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('تم حفظ قسم الخدمات بنجاح');
      navigate('/admin');
    } catch (err) {
      alert(err.response?.data?.error || 'فشل حفظ قسم الخدمات');
    } finally {
      setSaving(false);
    }
  };

  const header = useMemo(() => 'تعديل قسم الخدمات', []);

  if (loading) return <div className="admin-loading">جاري التحميل...</div>;

  return (
    <div className="admin-services-section-edit" dir="rtl">
      <div className="admin-services-section-edit-header">
        <Link to="/admin" className="back-link">
          <FaArrowRight /> العودة للصفحة الرئيسية
        </Link>
        <h1 className="admin-page-title">{header}</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-services-section-form">
        <div className="form-row">
          <label>نص الـ Label (h6)</label>
          <input
            type="text"
            className="form-input"
            value={form.section_label}
            onChange={(e) => handleChange('section_label', e.target.value)}
            required
          />
        </div>

        <div className="form-row">
          <label>نص الـ Heading (h2)</label>
          <textarea
            className="form-input"
            rows={3}
            value={form.section_heading}
            onChange={(e) => handleChange('section_heading', e.target.value)}
            required
          />
        </div>

        <div className="admin-services-section-images">
          <div className="admin-services-section-image-block">
            <div className="admin-services-section-image-label">الصورة الرئيسية</div>
            <Preview
              url={imageUrls.collage_main_image_url}
              label="بدون صورة"
            />
            <div className="admin-services-section-upload">
              <FaImage />
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleFile('main_image', e.target.files?.[0] || null)
                }
              />
            </div>
          </div>

          <div className="admin-services-section-image-block">
            <div className="admin-services-section-image-label">
              الصورة الثانوية
            </div>
            <Preview
              url={imageUrls.collage_secondary_image_url}
              label="بدون صورة"
            />
            <div className="admin-services-section-upload">
              <FaImage />
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  handleFile(
                    'secondary_image',
                    e.target.files?.[0] || null
                  )
                }
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="admin-btn-primary" disabled={saving}>
            {saving ? 'جاري الحفظ...' : 'حفظ'}
          </button>
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={() => navigate('/admin')}
            disabled={saving}
          >
            إلغاء
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminServicesSectionEdit;

